import React, { useState, useEffect, useRef } from 'react';
import { useConfig } from '../config/ConfigContext';
import { buildDriveStreamUrl } from '../utils/driveStreamUrl';

/**
 * Extract Google Drive file ID from various URL formats.
 * Supports: /file/d/{ID}, ?id={ID}, /uc?id={ID}, export links.
 * Detects and warns about folder URLs (which cannot be streamed as files).
 * @param {string} url - Google Drive URL
 * @returns {string|null} File ID or null
 */
const extractDriveFileId = (url) => {
  if (!url) return null;
  const trimmed = url.trim();

  // Detect folder URLs — these cannot be streamed as individual files
  if (/\/folders\/[a-zA-Z0-9_-]+/.test(trimmed)) {
    console.warn('[Viral Clips] Folder URL detected — cannot stream a folder. Use individual file URLs instead:', trimmed);
    return null;
  }

  // Standard file URL: /file/d/{ID}/...
  const fileMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return fileMatch[1];

  // Query param format: ?id={ID} or &id={ID} (Google Drive open/uc/export links)
  const idParamMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idParamMatch) return idParamMatch[1];

  // Direct download: /uc?export=download&id={ID}
  const ucMatch = trimmed.match(/\/uc\?.*id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return ucMatch[1];

  // Bare Google Drive ID (25-60 char alphanumeric string with hyphens/underscores)
  if (/^[a-zA-Z0-9_-]{25,}$/.test(trimmed)) return trimmed;

  console.warn('[Viral Clips] Unrecognized Drive URL format:', trimmed);
  return null;
};

/**
 * Parse newline-separated URLs from a single cell value
 * @param {string} rawValue - Raw cell value with newline-separated URLs
 * @returns {string[]} Array of cleaned URLs
 */
const parseMultilineUrls = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return [];
  return rawValue
    .split(/\n/)
    .map(url => url.trim())
    .filter(url => url.length > 0);
};

/**
 * Display labels for each clip type
 */
const TYPE_LABELS = {
  shorts: 'YouTube Shorts',
  reels: 'Instagram Reels',
  landscape: 'Featured Moments',
  raw: 'Raw Clips',
  clips: 'Key Moments',
};

/**
 * Parse type-prefixed URLs into grouped object.
 * Format: "shorts:https://...\\nlandscape:https://..."
 * Legacy URLs without prefix are grouped under 'clips'.
 * @param {string} rawValue
 * @returns {Object<string, string[]>}
 */
const parseTypedUrls = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return {};
  const lines = rawValue.split(/\n/).map(l => l.trim()).filter(l => l.length > 0);
  const grouped = {};
  lines.forEach(line => {
    const match = line.match(/^(\w+):(.+)$/);
    if (match && match[2].startsWith('http')) {
      const [, type, url] = match;
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(url);
    } else {
      // Legacy: no prefix → generic "clips" group
      if (!grouped['clips']) grouped['clips'] = [];
      grouped['clips'].push(line);
    }
  });
  return grouped;
};

/**
 * Build a shareable URL for a specific highlights item
 */
const buildShareUrl = (type, index) => {
  const base = `${window.location.origin}${window.location.pathname}`;
  const params = new URLSearchParams(window.location.search);
  params.set('tab', 'highlights');
  if (type === 'clip') {
    params.set('clip', index.toString());
  } else {
    params.delete('clip');
  }
  return `${base}?${params.toString()}`;
};

/**
 * ShareButton - uses Web Share API on mobile, dropdown on desktop.
 * When imageUrl is provided (infographics), tries to share the actual image file first.
 */
const ShareButton = ({ url, title, theme, imageUrl, getShareFile, shareText }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);
  const text = shareText || title;

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const handleClick = async () => {
    if (navigator.share) {
      // Try file sharing: canvas capture (getShareFile) or image URL fetch (imageUrl)
      let fileShared = false;
      if (getShareFile) {
        try {
          const file = await getShareFile();
          if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ title, text, url, files: [file] });
            fileShared = true;
          }
        } catch (e) {
          if (e.name === 'AbortError') return;
        }
      } else if (imageUrl) {
        fileShared = await shareImageFile(imageUrl, title, url, text);
      }
      if (fileShared) return;
      // Fallback: link-only sharing
      try { await navigator.share({ title, text, url }); } catch (e) { /* cancelled */ }
    } else {
      setShowMenu(prev => !prev);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); } catch (e) { /* fallback */ }
    setCopied(true);
    setTimeout(() => { setCopied(false); setShowMenu(false); }, 1500);
  };

  const socialLinks = [
    { name: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}` },
    { name: 'Twitter / X', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { name: 'Telegram', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  ];

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleClick}
        aria-label="Share"
        style={{
          background: 'none',
          border: `1px solid ${theme.colors.mediumGray || '#ddd'}`,
          borderRadius: '20px',
          padding: '5px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '0.8em',
          color: '#666',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
        Share
      </button>

      {showMenu && (
        <div style={{
          position: 'absolute',
          bottom: '110%',
          right: 0,
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          padding: '4px 0',
          zIndex: 100,
          minWidth: '160px',
          overflow: 'hidden'
        }}>
          <button
            onClick={copyLink}
            style={{
              display: 'block', width: '100%', padding: '10px 16px',
              border: 'none', background: 'none', textAlign: 'left',
              cursor: 'pointer', fontSize: '0.85em', color: copied ? theme.colors.primary : '#333',
              fontWeight: copied ? 'bold' : 'normal',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          {socialLinks.map(link => (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
              style={{
                display: 'block', padding: '10px 16px', textDecoration: 'none',
                fontSize: '0.85em', color: '#333',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {link.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// buildDriveStreamUrl imported from utils/driveStreamUrl.js

/**
 * Try to share an image file via Web Share API Level 2 (files).
 * Fetches image blob from the Drive proxy, creates a File, and shares it.
 * Returns true if file sharing succeeded; false if caller should fall back to link sharing.
 */
const shareImageFile = async (imageUrl, title, url, text) => {
  try {
    const resp = await fetch(imageUrl);
    if (!resp.ok) return false;
    const blob = await resp.blob();
    const ext = blob.type === 'image/png' ? '.png' : '.jpg';
    const file = new File([blob], `infographic${ext}`, { type: blob.type || 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      const shareData = { title, files: [file] };
      if (url) {
        shareData.url = url;
        shareData.text = text || title;
      }
      await navigator.share(shareData);
      return true;
    }
  } catch (e) {
    if (e.name === 'AbortError') return true; // user cancelled = still handled
  }
  return false;
};

/**
 * ClipOverlay - full-screen modal for streaming a viral clip with prev/next navigation.
 * Tries native HTML5 <video> via Drive stream proxy first; falls back to Google Drive
 * preview iframe on error or after 4s timeout (handles cases where Drive API returns
 * non-video responses or proxy isn't deployed).
 */
const ClipOverlay = ({ clips, clipTypes, currentIndex, onClose, onNavigate, theme, lectureTitle, config }) => {
  const fileId = extractDriveFileId(clips[currentIndex]);
  const streamUrl = buildDriveStreamUrl(fileId);
  const currentType = clipTypes?.[currentIndex];
  const isPortrait = currentType === 'shorts' || currentType === 'reels';
  const [videoReady, setVideoReady] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Reset ready state when navigating between clips
  useEffect(() => { setVideoReady(false); setUseFallback(false); setPermissionError(false); }, [currentIndex]);

  // Recover from page suspension (e.g., after sharing on mobile)
  useEffect(() => {
    const handleResume = () => {
      if (document.visibilityState === 'visible') {
        setVideoReady(false);
        setUseFallback(false);
        setReloadKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', handleResume);
    return () => document.removeEventListener('visibilitychange', handleResume);
  }, []);

  // Lock body scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Timeout fallback: if video doesn't load in 4s, switch to Drive preview iframe
  useEffect(() => {
    if (videoReady || useFallback) return;
    const timer = setTimeout(() => setUseFallback(true), 4000);
    return () => clearTimeout(timer);
  }, [currentIndex, reloadKey, videoReady, useFallback]);

  const shareUrl = buildShareUrl('clip', currentIndex);
  const shareTitle = `${lectureTitle || 'Lecture'} - Clip ${currentIndex + 1}`;

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, url: shareUrl }); } catch (e) { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(shareUrl); } catch (e) { /* fallback */ }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Key Moment ${currentIndex + 1}`}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.96)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Top bar: close + share */}
      <div style={{
        position: 'absolute', top: 16, right: 20,
        display: 'flex', alignItems: 'center', gap: '12px', zIndex: 10
      }}>
        {config?.share_clips_enabled !== false && (
          <button
            onClick={handleShare}
            aria-label="Share clip"
            style={{
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '20px',
              padding: '6px 14px', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: '0.85em',
              display: 'flex', alignItems: 'center', gap: '5px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            Share
          </button>
        )}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
            fontSize: '32px', cursor: 'pointer', lineHeight: 1, padding: '4px 8px',
            transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        >
          &times;
        </button>
      </div>

      {/* Clip counter */}
      {clips.length > 1 && (
        <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '12px', fontSize: '0.85em', letterSpacing: '0.5px' }}>
          Clip {currentIndex + 1} of {clips.length}
        </p>
      )}

      {/* Video + nav arrows */}
      <div style={{
        width: '100%', maxWidth: isPortrait ? '380px' : '850px',
        display: 'flex', alignItems: 'center', gap: '8px'
      }}>
        {/* Previous button */}
        {clips.length > 1 && (
          <button
            onClick={() => onNavigate(currentIndex > 0 ? currentIndex - 1 : clips.length - 1)}
            aria-label="Previous clip"
            style={{
              background: 'none', border: 'none', color: 'white',
              fontSize: '40px', cursor: 'pointer', padding: '10px 14px',
              opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.5; }}
          >
            &#8249;
          </button>
        )}

        {/* Video player area */}
        <div style={{ flex: 1 }}>
          <div style={{
            position: 'relative', paddingBottom: isPortrait ? '177.78%' : '56.25%', height: 0,
            borderRadius: '10px', overflow: 'hidden', backgroundColor: '#000',
            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          }}>
            {/* Loading indicator — visible until video is ready or fallback is active */}
            {!videoReady && !useFallback && (
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1,
              }}>
                <div style={{
                  width: '40px', height: '40px',
                  border: '3px solid rgba(255,255,255,0.15)',
                  borderTopColor: 'rgba(255,255,255,0.8)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              </div>
            )}

            {permissionError ? (
              /* Permission error — file not publicly shared */
              <div style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                color: 'rgba(255,255,255,0.7)', padding: '20px', textAlign: 'center',
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '12px', opacity: 0.5 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <p style={{ margin: '0 0 8px', fontSize: '0.95em' }}>This clip is not publicly accessible</p>
                <p style={{ margin: 0, fontSize: '0.8em', opacity: 0.6 }}>
                  The file needs to be shared as &ldquo;Anyone with the link&rdquo; in Google Drive
                </p>
              </div>
            ) : !useFallback ? (
              /* Native HTML5 video — streams via Google Drive API; falls back to iframe on error */
              <video
                key={`video-${currentIndex}-${reloadKey}`}
                controls
                autoPlay
                playsInline
                style={{
                  position: 'absolute', top: 0, left: 0,
                  width: '100%', height: '100%',
                  backgroundColor: '#000',
                  objectFit: 'contain',
                  opacity: videoReady ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
                onCanPlay={() => setVideoReady(true)}
                onError={async () => {
                  try {
                    const resp = await fetch(streamUrl, { method: 'HEAD' });
                    if (!resp.ok) {
                      // Any non-2xx from Drive API means file can't be streamed.
                      // Google returns 404 (not 403) for private files via API key
                      // to hide file existence. Treat all 4xx/5xx as access errors
                      // since iframe fallback won't help either — it just shows
                      // a Google "Sign in" page for private files.
                      setPermissionError(true);
                      return;
                    }
                  } catch (e) { /* network error — try iframe fallback */ }
                  setUseFallback(true);
                }}
              >
                <source src={streamUrl} type="video/mp4" />
              </video>
            ) : (
              /* Fallback: Google Drive embedded video player */
              <iframe
                key={`iframe-${currentIndex}-${reloadKey}`}
                src={`https://drive.google.com/file/d/${fileId}/preview`}
                title={`Clip ${currentIndex + 1}`}
                allow="autoplay"
                style={{
                  position: 'absolute', top: 0, left: 0,
                  width: '100%', height: '100%',
                  border: 'none',
                }}
              />
            )}
          </div>
        </div>

        {/* Next button */}
        {clips.length > 1 && (
          <button
            onClick={() => onNavigate(currentIndex < clips.length - 1 ? currentIndex + 1 : 0)}
            aria-label="Next clip"
            style={{
              background: 'none', border: 'none', color: 'white',
              fontSize: '40px', cursor: 'pointer', padding: '10px 14px',
              opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.5; }}
          >
            &#8250;
          </button>
        )}
      </div>

      {/* Inline CSS for animations */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

/**
 * InfographicOverlay - full-screen modal for viewing an infographic in-app.
 * Loads the image directly via Drive stream proxy (no Google Drive chrome).
 */
const InfographicOverlay = ({ infographics, currentIndex, onClose, onNavigate, theme, lectureTitle, config }) => {
  const fileId = extractDriveFileId(infographics[currentIndex]);
  const streamUrl = buildDriveStreamUrl(fileId);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => { setImageLoaded(false); setUseFallback(false); setZoomed(false); }, [currentIndex]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { if (zoomed) { setZoomed(false); } else { onClose(); } } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, zoomed]);

  const toggleZoom = () => {
    const next = !zoomed;
    setZoomed(next);
    // When zooming in, scroll to center of image
    if (next && scrollContainerRef.current) {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
          el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
        }
      });
    }
  };

  const shareUrl = buildShareUrl('infographic', currentIndex) + (fileId ? `&ogimg=${fileId}` : '');
  const handleShare = async () => {
    const title = `${lectureTitle || 'Lecture'} - Infographic`;
    const text = `Check out this infographic from "${lectureTitle || 'Lecture'}"`;
    if (navigator.share) {
      const shared = await shareImageFile(streamUrl, title, shareUrl, text);
      if (shared) return;
      try { await navigator.share({ title, text, url: shareUrl }); } catch (e) { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(shareUrl); } catch (e) { /* fallback */ }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Infographic ${currentIndex + 1}`}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.96)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { if (zoomed) setZoomed(false); else onClose(); } }}
    >
      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 16, right: 20,
        display: 'flex', alignItems: 'center', gap: '12px', zIndex: 10
      }}>
        {/* Zoom toggle button */}
        {imageLoaded && !useFallback && (
          <button onClick={toggleZoom} aria-label={zoomed ? 'Zoom out' : 'Zoom in'} style={{
            background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '20px',
            padding: '6px 14px', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: '0.85em',
            display: 'flex', alignItems: 'center', gap: '5px', transition: 'background-color 0.2s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              {!zoomed && <><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></>}
              {zoomed && <line x1="8" y1="11" x2="14" y2="11" />}
            </svg>
            {zoomed ? 'Zoom Out' : 'Zoom In'}
          </button>
        )}
        {config?.share_infographics_enabled !== false && (
          <button onClick={handleShare} aria-label="Share infographic" style={{
            background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '20px',
            padding: '6px 14px', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: '0.85em',
            display: 'flex', alignItems: 'center', gap: '5px', transition: 'background-color 0.2s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            Share
          </button>
        )}
        <button onClick={onClose} aria-label="Close" style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
          fontSize: '32px', cursor: 'pointer', lineHeight: 1, padding: '4px 8px', transition: 'color 0.2s',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        >&times;</button>
      </div>

      {/* Counter */}
      {infographics.length > 1 && !zoomed && (
        <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '12px', fontSize: '0.85em', letterSpacing: '0.5px' }}>
          {currentIndex + 1} of {infographics.length}
        </p>
      )}

      {/* Image + nav arrows */}
      <div style={{
        width: '100%', maxWidth: zoomed ? '100%' : '900px',
        display: 'flex', alignItems: zoomed ? 'stretch' : 'center', gap: '8px',
        flex: zoomed ? 1 : 'none', minHeight: 0,
      }}>
        {infographics.length > 1 && !zoomed && (
          <button
            onClick={() => onNavigate(currentIndex > 0 ? currentIndex - 1 : infographics.length - 1)}
            aria-label="Previous" style={{
              background: 'none', border: 'none', color: 'white', fontSize: '40px',
              cursor: 'pointer', padding: '10px 14px', opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.5; }}
          >&#8249;</button>
        )}

        <div
          ref={scrollContainerRef}
          style={{
            flex: 1, display: 'flex',
            alignItems: zoomed ? 'flex-start' : 'center',
            justifyContent: zoomed ? 'flex-start' : 'center',
            maxHeight: zoomed ? '100%' : 'calc(100vh - 120px)',
            overflow: zoomed ? 'auto' : 'hidden',
            WebkitOverflowScrolling: 'touch',
            cursor: zoomed ? 'grab' : 'zoom-in',
          }}
          onDoubleClick={toggleZoom}
        >
          {!imageLoaded && !useFallback && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              <div style={{
                width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.15)',
                borderTopColor: 'rgba(255,255,255,0.8)', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
            </div>
          )}
          {!useFallback ? (
            <img
              key={`img-${currentIndex}`}
              src={streamUrl}
              alt={`Infographic ${currentIndex + 1}`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setUseFallback(true)}
              draggable={false}
              style={{
                maxWidth: zoomed ? 'none' : '100%',
                maxHeight: zoomed ? 'none' : 'calc(100vh - 120px)',
                width: zoomed ? 'auto' : undefined,
                height: zoomed ? 'auto' : undefined,
                objectFit: 'contain', borderRadius: '8px',
                display: imageLoaded ? 'block' : 'none',
                transition: 'max-width 0.3s ease, max-height 0.3s ease',
              }}
            />
          ) : (
            <iframe
              src={`https://drive.google.com/file/d/${fileId}/preview`}
              title={`Infographic ${currentIndex + 1}`}
              style={{ width: '100%', height: 'calc(100vh - 120px)', border: 'none', borderRadius: '8px' }}
            />
          )}
        </div>

        {infographics.length > 1 && !zoomed && (
          <button
            onClick={() => onNavigate(currentIndex < infographics.length - 1 ? currentIndex + 1 : 0)}
            aria-label="Next" style={{
              background: 'none', border: 'none', color: 'white', fontSize: '40px',
              cursor: 'pointer', padding: '10px 14px', opacity: 0.5, transition: 'opacity 0.2s', flexShrink: 0
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.5; }}
          >&#8250;</button>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/**
 * ClipCard - individual clip thumbnail card with IntersectionObserver-based loading.
 * Loads video metadata (and seeks to 1s for title card) when the card scrolls into view.
 * Works on both desktop (before hover) and mobile (on scroll).
 */
const ClipCard = ({ fileId, index, totalClips, onPlay, theme, lectureTitle, ogImageId, aspectRatio, config }) => {
  const paddingBottom = aspectRatio === '9:16' ? '177.78%' : '56.25%';
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !video.src) {
          // Stagger requests by 400ms per clip to avoid burst hits to Google Drive API
          setTimeout(() => {
            video.src = buildDriveStreamUrl(fileId);
            video.preload = 'metadata';
            // Force iOS Safari to load video data — muted+playsInline
            // videos are allowed to autoplay without user gesture
            video.play().catch(() => {});
          }, index * 400);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [fileId]);

  return (
    <div style={{ width: '100%', maxWidth: '600px' }}>
      {/* Header row: clip label + share button */}
      <div style={{
        display: 'flex',
        justifyContent: totalClips > 1 ? 'space-between' : 'flex-end',
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        {totalClips > 1 && (
          <p style={{ fontSize: '0.9em', color: '#666', margin: 0 }}>
            Clip {index + 1} of {totalClips}
          </p>
        )}
        {config?.share_clips_enabled !== false && (
          <ShareButton
            url={buildShareUrl('clip', index) + (ogImageId ? `&ogimg=${ogImageId}` : '')}
            title={`${lectureTitle || 'Lecture'} - Clip ${index + 1}`}
            shareText={`Check out this clip from "${lectureTitle || 'Lecture'}"`}
            theme={theme}
            getShareFile={async () => {
              const video = videoRef.current;
              if (!video || video.readyState < 2 || !video.videoWidth) return null;
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
              const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.85));
              if (!blob) return null;
              return new File([blob], `clip-${index + 1}.jpg`, { type: 'image/jpeg' });
            }}
          />
        )}
      </div>

      {/* Styled clip card with gradient and play button */}
      <div
        onClick={() => onPlay(index)}
        role="button"
        tabIndex={0}
        aria-label={`Play clip ${index + 1}`}
        onKeyDown={(e) => { if (e.key === 'Enter') onPlay(index); }}
        style={{
          position: 'relative',
          paddingBottom,
          height: 0,
          overflow: 'hidden',
          borderRadius: theme.borderRadius?.medium || '8px',
          background: `linear-gradient(135deg, ${theme.colors.primary || '#1a237e'} 0%, #0d1117 70%, #161b22 100%)`,
          cursor: 'pointer',
          transition: 'box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        {/* Film strip placeholder — visible until video thumbnail loads */}
        <svg
          viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"
          style={{ position: 'absolute', top: '15%', left: '8%', width: '30%', height: '70%' }}
        >
          <rect x="2" y="2" width="20" height="20" rx="2" />
          <line x1="7" y1="2" x2="7" y2="22" /><line x1="17" y1="2" x2="17" y2="22" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <rect x="7" y="2" width="10" height="10" /><rect x="7" y="12" width="10" height="10" />
        </svg>

        {/* Video thumbnail — src set by IntersectionObserver when card scrolls into view */}
        <video
          ref={videoRef}
          muted
          playsInline
          onLoadedMetadata={(e) => { e.target.currentTime = 1; }}
          onSeeked={(e) => { e.target.pause(); e.target.style.opacity = '1'; }}
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            opacity: 0,
            transition: 'opacity 0.4s ease',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        {/* Play button circle */}
        <div style={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '68px', height: '68px',
          backgroundColor: 'rgba(255,255,255,0.92)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          <div style={{
            width: 0, height: 0,
            borderLeft: '22px solid #333',
            borderTop: '13px solid transparent',
            borderBottom: '13px solid transparent',
            marginLeft: '5px'
          }} />
        </div>

        {/* "Click to play" hint */}
        <p style={{
          position: 'absolute',
          bottom: '12px', left: 0, right: 0,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.5)',
          fontSize: '0.8em',
          margin: 0,
          zIndex: 2,
          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
        }}>
          Click to play
        </p>
      </div>
    </div>
  );
};

/**
 * ClipTypeSection - horizontal-scroll boxed section for a single clip type.
 * Follows the VideoHighlights.js boxed section pattern with scroll arrows.
 */
const ClipTypeSection = ({ type, urls, globalOffset, onPlay, theme, lectureTitle, ogImageId, config }) => {
  const scrollRef = useRef(null);
  const isPortrait = type === 'shorts' || type === 'reels';
  const cardWidth = isPortrait ? 160 : 280;

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction * (cardWidth + 16) * 2, behavior: 'smooth' });
    }
  };

  return (
    <div style={{
      background: '#f8f9fa',
      borderRadius: '16px',
      padding: '20px',
      border: '1px solid #e8e8e8',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      {/* Header: type label + count badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <h3 style={{
          margin: 0, fontSize: '1.1em',
          color: theme.colors.primary,
          fontFamily: theme.fonts.secondary,
        }}>
          {TYPE_LABELS[type] || type}
        </h3>
        <span style={{
          background: theme.colors.primary, color: '#fff',
          borderRadius: '12px', padding: '2px 10px',
          fontSize: '0.75em', fontWeight: 'bold',
        }}>
          {urls.length}
        </span>
      </div>

      {/* Scrollable row with arrow buttons */}
      <div style={{ position: 'relative' }}>
        {urls.length > 2 && (
          <button
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
            style={{
              position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 5, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%',
              width: 36, height: 36, cursor: 'pointer', color: '#fff', fontSize: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.55)'; }}
          >&#8249;</button>
        )}

        <div
          ref={scrollRef}
          className="clip-type-scroll"
          style={{
            display: 'flex', gap: '16px',
            overflowX: 'auto', scrollBehavior: 'smooth',
            paddingBottom: '4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {urls.map((url, index) => {
            const fileId = extractDriveFileId(url);
            if (!fileId) return null;
            return (
              <div key={index} style={{ flexShrink: 0, width: cardWidth }}>
                <ClipCard
                  fileId={fileId}
                  index={globalOffset + index}
                  totalClips={0}
                  onPlay={onPlay}
                  theme={theme}
                  lectureTitle={lectureTitle}
                  ogImageId={ogImageId}
                  aspectRatio={isPortrait ? '9:16' : '16:9'}
                  config={config}
                />
              </div>
            );
          })}
        </div>

        {urls.length > 2 && (
          <button
            onClick={() => scroll(1)}
            aria-label="Scroll right"
            style={{
              position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 5, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%',
              width: 36, height: 36, cursor: 'pointer', color: '#fff', fontSize: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.55)'; }}
          >&#8250;</button>
        )}
      </div>

      {/* Hide scrollbar for WebKit browsers */}
      <style>{`.clip-type-scroll::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
};

/**
 * LectureHighlights component - displays infographics and viral clips
 * with share buttons, clip overlay modal, and thumbnail-based clips for performance.
 * Supports type-prefixed URLs for grouped display (shorts, reels, landscape, raw).
 */
const LectureHighlights = ({ infographicsUrls, viralClipsUrls, dubbedVideoUrl, initialClipIndex, lectureTitle }) => {
  const { theme, config } = useConfig();
  const [overlayClipIndex, setOverlayClipIndex] = useState(null);
  const [overlayInfographicIndex, setOverlayInfographicIndex] = useState(null);
  const deepLinkConsumed = useRef(false);

  const infographics = parseMultilineUrls(infographicsUrls);

  // V8.4: AI-dubbed video (English, speaker's cloned voice) — single Drive file
  const dubbedVideoFileId = dubbedVideoUrl ? extractDriveFileId(dubbedVideoUrl) : null;

  // Parse typed URLs — groups clips by type (shorts, landscape, etc.)
  // Legacy URLs without prefix are grouped under 'clips'
  const clipsByType = parseTypedUrls(viralClipsUrls);
  const typeOrder = Object.keys(clipsByType);
  // Flat array for overlay prev/next navigation across all types
  const allClips = typeOrder.flatMap(type => clipsByType[type]);
  // Parallel array tracking the type for each clip (used by ClipOverlay for aspect ratio)
  const allClipTypes = typeOrder.flatMap(type => clipsByType[type].map(() => type));
  const hasMultipleTypes = typeOrder.length > 1 || (typeOrder.length === 1 && typeOrder[0] !== 'clips');

  // First infographic file ID — used as OG preview image for clip share links
  const firstInfographicId = infographics.length > 0 ? extractDriveFileId(infographics[0]) : null;

  // Pre-validate: how many URLs yield valid Drive file IDs?
  // Prevents rendering empty "Key Moments" section when all URLs are invalid
  const validClipCount = allClips.filter(url => extractDriveFileId(url) !== null).length;

  // Diagnostic: warn when URLs were provided but none could be parsed
  useEffect(() => {
    if (allClips.length > 0 && validClipCount === 0) {
      console.warn(
        `[Viral Clips] ${allClips.length} URL(s) provided but none yielded valid Drive file IDs.\n` +
        `Raw URLs:\n${allClips.map(u => `  - ${u}`).join('\n')}\n` +
        `Common causes: folder URLs instead of file URLs, unsupported URL formats, or files not shared publicly.`
      );
    }
  }, [allClips.length, validClipCount]);

  // Open overlay for deep-linked clip (one-time on mount)
  useEffect(() => {
    if (
      !deepLinkConsumed.current &&
      initialClipIndex !== null &&
      initialClipIndex !== undefined &&
      initialClipIndex >= 0 &&
      initialClipIndex < allClips.length
    ) {
      setOverlayClipIndex(initialClipIndex);
      deepLinkConsumed.current = true;
    }
  }, [initialClipIndex, allClips.length]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* V8.4: Dubbed Video Section */}
      {dubbedVideoFileId && (
        <div>
          <h2 style={{
            color: theme.colors.primary,
            marginBottom: '16px',
            textAlign: 'center',
            fontFamily: theme.fonts.secondary
          }}>
            Dubbed Lecture (English)
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: '100%',
              maxWidth: '900px',
              borderRadius: theme.borderRadius?.medium || '8px',
              overflow: 'hidden',
              backgroundColor: '#000'
            }}>
              <video
                controls
                preload="metadata"
                playsInline
                src={buildDriveStreamUrl(dubbedVideoFileId)}
                style={{ width: '100%', display: 'block', aspectRatio: '16 / 9' }}
                title={lectureTitle ? `${lectureTitle} — English dub` : 'Dubbed lecture (English)'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Infographics Section */}
      {infographics.length > 0 && (
        <div>
          <h2 style={{
            color: theme.colors.primary,
            marginBottom: '16px',
            textAlign: 'center',
            fontFamily: theme.fonts.secondary
          }}>
            {infographics.length === 1 ? 'Infographic' : 'Infographics'}
          </h2>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            {infographics.map((url, index) => {
              const fileId = extractDriveFileId(url);
              if (!fileId) return null;
              return (
                <div key={index} style={{ width: '100%', maxWidth: '700px' }}>
                  <div style={{
                    position: 'relative',
                    borderRadius: theme.borderRadius?.medium || '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    backgroundColor: '#fff'
                  }}>
                    <iframe
                      src={`https://drive.google.com/file/d/${fileId}/preview`}
                      title={`Infographic ${index + 1}`}
                      style={{ width: '100%', height: '600px', border: 'none' }}
                    />
                    {/* Fullscreen button overlay */}
                    <button
                      onClick={() => setOverlayInfographicIndex(index)}
                      aria-label="View fullscreen"
                      style={{
                        position: 'absolute', top: '10px', right: '10px',
                        background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '8px',
                        padding: '8px 12px', cursor: 'pointer', color: 'white', fontSize: '0.8em',
                        display: 'flex', alignItems: 'center', gap: '5px', zIndex: 5,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                        <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                      </svg>
                      Full Screen
                    </button>
                  </div>
                  {config?.share_infographics_enabled !== false && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <ShareButton
                        url={buildShareUrl('infographic', index) + (fileId ? `&ogimg=${fileId}` : '')}
                        title={`${lectureTitle || 'Lecture'} - Infographic`}
                        shareText={`Check out this infographic from "${lectureTitle || 'Lecture'}"`}
                        theme={theme}
                        imageUrl={buildDriveStreamUrl(fileId)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Viral Clips Section — only render if at least one clip has a valid Drive file ID */}
      {validClipCount > 0 && (
        <div>
          <h2 style={{
            color: theme.colors.primary,
            marginBottom: '16px',
            textAlign: 'center',
            fontFamily: theme.fonts.secondary
          }}>
            {allClips.length === 1 ? 'Key Moment' : 'Key Moments'}
          </h2>

          {hasMultipleTypes ? (
            /* Multi-type: boxed horizontal-scroll sections per type */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {(() => {
                let globalOffset = 0;
                return typeOrder.map(type => {
                  const urls = clipsByType[type];
                  const offset = globalOffset;
                  globalOffset += urls.length;
                  return (
                    <ClipTypeSection
                      key={type}
                      type={type}
                      urls={urls}
                      globalOffset={offset}
                      onPlay={setOverlayClipIndex}
                      theme={theme}
                      lectureTitle={lectureTitle}
                      ogImageId={firstInfographicId}
                      config={config}
                    />
                  );
                });
              })()}
            </div>
          ) : (
            /* Single type or legacy: vertical stack (backward compatible) */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px'
            }}>
              {allClips.map((url, index) => {
                const fileId = extractDriveFileId(url);
                if (!fileId) return null;
                return (
                  <ClipCard
                    key={index}
                    fileId={fileId}
                    index={index}
                    totalClips={allClips.length}
                    onPlay={setOverlayClipIndex}
                    theme={theme}
                    lectureTitle={lectureTitle}
                    ogImageId={firstInfographicId}
                    config={config}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Infographic Overlay Modal */}
      {overlayInfographicIndex !== null && overlayInfographicIndex >= 0 && overlayInfographicIndex < infographics.length && (
        <InfographicOverlay
          infographics={infographics}
          currentIndex={overlayInfographicIndex}
          onClose={() => setOverlayInfographicIndex(null)}
          onNavigate={setOverlayInfographicIndex}
          theme={theme}
          lectureTitle={lectureTitle}
          config={config}
        />
      )}

      {/* Clip Overlay Modal — uses flat allClips array for cross-type navigation */}
      {overlayClipIndex !== null && overlayClipIndex >= 0 && overlayClipIndex < allClips.length && (
        <ClipOverlay
          clips={allClips}
          clipTypes={allClipTypes}
          currentIndex={overlayClipIndex}
          onClose={() => setOverlayClipIndex(null)}
          onNavigate={setOverlayClipIndex}
          theme={theme}
          lectureTitle={lectureTitle}
          config={config}
        />
      )}
    </div>
  );
};

export default LectureHighlights;
